import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { StatusPill } from "@/components/PortalShell";
import { X, Star, Trash2 } from "lucide-react";

const Card = ({ children, ...p }) => <div className="card-soft" style={{ padding: 22 }} {...p}>{children}</div>;
const fmtDate = (d) => { if (!d) return "No date"; const dt = new Date(d); return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "2-digit", year: "2-digit" }); };
const nextDay = (d) => { const dt = new Date(d + "T00:00:00"); dt.setDate(dt.getDate() + 1); return dt.toISOString().slice(0, 10); };
const jobGcal = (j) => { const s = (j.scheduled_date || "").replace(/-/g, ""); const e = nextDay(j.scheduled_date).replace(/-/g, ""); const det = `${j.contractor_name || ""} (${j.contractor_trade || ""})${j.scheduled_time ? " · " + j.scheduled_time : ""}`; return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`${j.title} · ${j.property_name || ""}`)}&dates=${s}/${e}&details=${encodeURIComponent(det)}`; };
const jobIcs = (j) => { const s = (j.scheduled_date || "").replace(/-/g, ""); const e = nextDay(j.scheduled_date).replace(/-/g, ""); const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Solvana//EN", "BEGIN:VEVENT", `UID:${j.job_id}@solvanagroup.com`, `DTSTART;VALUE=DATE:${s}`, `DTEND;VALUE=DATE:${e}`, `SUMMARY:${j.title} · ${j.property_name || ""}`, `DESCRIPTION:${j.contractor_name || ""} (${j.contractor_trade || ""})${j.scheduled_time ? " · " + j.scheduled_time : ""}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n"); const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" })); const a = document.createElement("a"); a.href = url; a.download = `solvana-job-${j.scheduled_date || "visit"}.ics`; a.click(); URL.revokeObjectURL(url); };

function Stars({ value = 0, onRate }) {
  const [hover, setHover] = useState(0);
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        return <button key={n} type="button" onClick={() => onRate && onRate(n)} onMouseEnter={() => onRate && setHover(n)} onMouseLeave={() => setHover(0)} data-testid={onRate ? `star-${n}` : undefined} style={{ background: "none", border: "none", cursor: onRate ? "pointer" : "default", padding: 0, lineHeight: 0 }}><Star size={16} color="#C79A4B" fill={active ? "#C79A4B" : "none"} /></button>;
      })}
    </span>
  );
}

export default function Contractors({ properties }) {
  const [contractors, setContractors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [propFilter, setPropFilter] = useState("");
  const load = useCallback(async () => {
    try { const [c, j] = await Promise.all([api.get("/contractors"), api.get("/jobs")]); setContractors(c.data); setJobs(j.data); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const shownJobs = jobs.filter((j) => !propFilter || j.property_id === propFilter);

  return (
    <div style={{ display: "grid", gap: 30 }}>
      <AssignJob contractors={contractors} properties={properties} reload={load} />
      <Directory contractors={contractors} reload={load} />
      <JobsBoard jobs={shownJobs} properties={properties} propFilter={propFilter} setPropFilter={setPropFilter} reload={load} />
    </div>
  );
}

function LeaderboardDropdown({ contractors }) {
  const [open, setOpen] = useState(false);
  const ranked = contractors.filter((c) => (c.completed_jobs || 0) > 0).sort((a, b) => {
    const ar = a.avg_rating || 0, br = b.avg_rating || 0;
    if (br !== ar) return br - ar;
    const at = a.avg_turnaround_days == null ? 999 : a.avg_turnaround_days, bt = b.avg_turnaround_days == null ? 999 : b.avg_turnaround_days;
    if (at !== bt) return at - bt;
    return (b.completed_jobs || 0) - (a.completed_jobs || 0);
  }).slice(0, 10);
  return (
    <div style={{ position: "relative" }}>
      <button type="button" className="btn-outline" style={{ padding: "8px 14px", fontSize: 13, whiteSpace: "nowrap" }} onClick={() => setOpen((o) => !o)} data-testid="leaderboard-toggle">Leaderboard</button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 30, width: 340, background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 10, boxShadow: "0 12px 30px rgba(0,0,0,.14)", padding: 14 }} data-testid="contractor-leaderboard">
          <div className="lbl" style={{ marginBottom: 4 }}>Top contractors</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>Ranked by rating, then speed and volume.</div>
          {ranked.length === 0 ? <p style={{ fontSize: 13, color: "var(--muted)" }}>No completed jobs yet.</p> : ranked.map((c, i) => (
            <div key={c.contractor_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: i < ranked.length - 1 ? "1px solid var(--line)" : "none" }}>
              <span className="font-serif-c" style={{ fontSize: 17, width: 20, color: i === 0 ? "#C79A4B" : "var(--muted)" }}>{i + 1}</span>
              <span style={{ flex: 1, minWidth: 0 }}><b style={{ fontSize: 13 }}>{c.name}</b><span className="lbl-m" style={{ color: "var(--muted)", display: "block" }}>{c.trade}</span></span>
              <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", fontSize: 12, color: "var(--muted)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Stars value={Math.round(c.avg_rating || 0)} />{c.avg_rating ? ` ${c.avg_rating}` : ""}</span>
                <span>{c.avg_turnaround_days != null ? `${c.avg_turnaround_days}d · ` : ""}{c.completed_jobs} done</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Directory({ contractors, reload }) {
  const EMPTY = { name: "", trade: "", company: "", phone: "", email: "", notes: "" };
  const [show, setShow] = useState(false);
  const [q, setQ] = useState("");
  const [f, setF] = useState(EMPTY);
  const filtered = contractors.filter((c) => { const s = q.trim().toLowerCase(); return !s || `${c.name} ${c.trade} ${c.company || ""}`.toLowerCase().includes(s); });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/contractors", f); toast.success("Contractor added"); setF(EMPTY); setShow(false); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div className="lbl">Trusted contractors</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <LeaderboardDropdown contractors={contractors} />
          <input className="inp" placeholder="Search by name or trade…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="contractor-search" style={{ minWidth: 220 }} />
          {!show && <button className="btn-ink" onClick={() => setShow(true)} data-testid="add-contractor-btn">Add contractor</button>}
        </div>
      </div>
      {show && (
        <form onSubmit={submit} className="card-soft" style={{ padding: 22, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, maxWidth: 640 }}>
          <input className="inp" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="contractor-name" />
          <input className="inp" placeholder="Trade (e.g. Pool, Cleaning, Electrician)" value={f.trade} onChange={(e) => setF({ ...f, trade: e.target.value })} required data-testid="contractor-trade" />
          <input className="inp" placeholder="Company (optional)" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} />
          <input className="inp" placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          <input className="inp" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <input className="inp" placeholder="Notes (zones, availability)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          <div style={{ gridColumn: "1/3", display: "flex", gap: 12 }}><button className="btn-ink" type="submit">Save</button><button className="btn-outline" type="button" onClick={() => setShow(false)}>Cancel</button></div>
        </form>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 16 }}>
        {filtered.map((c) => <ContractorCard key={c.contractor_id} c={c} reload={reload} />)}
        {filtered.length === 0 && <p style={{ color: "var(--muted)" }}>{contractors.length === 0 ? "No contractors yet." : "No contractors match your search."}</p>}
      </div>
    </div>
  );
}

function ContractorCard({ c, reload }) {
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState(c.email || "");
  const [link, setLink] = useState("");
  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/contractors/${c.contractor_id}/invite`, { email });
      if (data.invited) { toast.success(`Invite emailed to ${data.email}`); setShowLogin(false); setLink(""); }
      else { toast.error("We couldn't email that address. Copy the link below and send it to them directly."); setLink(data.link || ""); }
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${c.name}? This removes their directory entry and portal access. Contractors with completed (invoiced) work can't be deleted.`)) return;
    try { await api.delete(`/contractors/${c.contractor_id}`); toast.success("Contractor removed"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card data-testid="contractor-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
        <div>
          <b style={{ fontSize: 17 }}>{c.name}</b>
          <div className="lbl-m" style={{ color: "var(--honey)", marginTop: 2 }}>{c.trade}</div>
        </div>
        {c.open_jobs > 0 && <span className="pill-tag" style={{ background: "#f3e4cf", color: "#8a5a1f" }}>{c.open_jobs} open</span>}
        <button onClick={del} data-testid="delete-contractor-btn" title="Delete contractor" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", marginLeft: 8, flexShrink: 0 }}><Trash2 size={15} /></button>
      </div>
      {c.company && <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 6 }}>{c.company}</div>}
      <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{[c.phone, c.email].filter(Boolean).join(" · ")}</div>
      <div style={{ display: "flex", gap: 14, marginTop: 8, fontSize: 12, color: "var(--muted)", alignItems: "center", flexWrap: "wrap" }} data-testid="contractor-performance">
        <span><b style={{ color: "var(--ink)" }}>{c.completed_jobs || 0}</b> completed</span>
        {c.avg_turnaround_days != null && <span><b style={{ color: "var(--ink)" }}>{c.avg_turnaround_days}d</b> avg turnaround</span>}
        {c.avg_rating != null && <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Stars value={Math.round(c.avg_rating)} /> {c.avg_rating}</span>}
      </div>
      {c.notes && <p style={{ fontSize: 13, marginTop: 8 }}>{c.notes}</p>}
      <div style={{ marginTop: 12, borderTop: "1px solid var(--line)", paddingTop: 12 }}>
        {c.has_login ? (
          <div style={{ fontSize: 13, color: "#5B6440" }}>Login active · {c.email}</div>
        ) : !showLogin ? (
          <button className="btn-outline" style={{ padding: "6px 14px", fontSize: 13 }} onClick={() => setShowLogin(true)} data-testid="create-login-btn">Invite to portal</button>
        ) : (
          <form onSubmit={sendInvite} style={{ display: "grid", gap: 8 }}>
            <input className="inp" type="email" placeholder="Contractor's email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
            <div style={{ fontSize: 11, color: "var(--muted)" }}>We'll email them a link to set their own password, just like owners.</div>
            <div style={{ display: "flex", gap: 8 }}><button className="btn-ink" type="submit" style={{ padding: "6px 14px", fontSize: 13 }} data-testid="save-login-btn">Send invite</button><button className="btn-outline" type="button" style={{ padding: "6px 14px", fontSize: 13 }} onClick={() => { setShowLogin(false); setLink(""); }}>Cancel</button></div>
          </form>
        )}
        {link && (
          <div style={{ marginTop: 10, background: "var(--soft)", border: "1px solid var(--line)", borderRadius: 8, padding: 10 }} data-testid="invite-link-box">
            <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>Email didn't send. Copy this set-password link and send it to the contractor:</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="inp" readOnly value={link} onFocus={(e) => e.target.select()} style={{ fontSize: 11, flex: 1 }} data-testid="invite-link" />
              <button className="btn-ink" type="button" style={{ padding: "6px 12px", fontSize: 12, flexShrink: 0 }} onClick={() => { navigator.clipboard.writeText(link).catch(() => {}); toast.success("Link copied"); }} data-testid="copy-invite-link">Copy</button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function AssignJob({ contractors, properties, reload }) {
  const EMPTY = { property_id: "", contractor_id: "", title: "", description: "", scheduled_date: "", scheduled_time: "", cost: "", issue_id: "" };
  const [f, setF] = useState(EMPTY);
  const [issues, setIssues] = useState([]);
  useEffect(() => {
    if (!f.property_id) { setIssues([]); return; }
    api.get(`/issues?property_id=${f.property_id}`).then((r) => setIssues(r.data.filter((i) => i.status !== "resolved"))).catch(() => setIssues([]));
  }, [f.property_id]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.property_id || !f.contractor_id) return toast.error("Pick a property and contractor");
    try { await api.post("/jobs", { ...f, issue_id: f.issue_id || null, cost: f.cost ? parseFloat(f.cost) : null }); toast.success("Job assigned"); setF(EMPTY); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <form onSubmit={submit} className="card-soft" style={{ padding: 22, display: "grid", gap: 12, maxWidth: 640 }} data-testid="assign-job-form">
      <div className="lbl">Assign a job</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <select className="inp" value={f.property_id} onChange={(e) => setF({ ...f, property_id: e.target.value, issue_id: "" })} required data-testid="job-property">
          <option value="">Select property…</option>
          {properties.map((p) => <option key={p.property_id} value={p.property_id}>{p.ref ? `${p.ref} · ` : ""}{p.name}</option>)}
        </select>
        <select className="inp" value={f.contractor_id} onChange={(e) => setF({ ...f, contractor_id: e.target.value })} required data-testid="job-contractor">
          <option value="">Select contractor…</option>
          {contractors.map((c) => <option key={c.contractor_id} value={c.contractor_id}>{c.name} · {c.trade}</option>)}
        </select>
      </div>
      <input className="inp" placeholder="Job title (e.g. Weekly pool clean)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required data-testid="job-title" />
      <input className="inp" type="number" step="0.01" placeholder="Cost € (optional, flows to the invoice)" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} data-testid="job-cost" />
      <textarea className="inp" rows={2} placeholder="Details / instructions (optional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><div className="lbl-m" style={{ marginBottom: 4 }}>Scheduled date</div><input className="inp" type="date" value={f.scheduled_date} onChange={(e) => setF({ ...f, scheduled_date: e.target.value })} data-testid="job-date" /></div>
        <div><div className="lbl-m" style={{ marginBottom: 4 }}>Time slot</div>
          <select className="inp" value={f.scheduled_time} onChange={(e) => setF({ ...f, scheduled_time: e.target.value })} data-testid="job-time">
            <option value="">Any time</option>
            <option>Morning</option><option>Afternoon</option><option>Evening</option>
          </select>
        </div>
      </div>
      <div><div className="lbl-m" style={{ marginBottom: 4 }}>Link to an issue (optional)</div>
        <select className="inp" value={f.issue_id} onChange={(e) => setF({ ...f, issue_id: e.target.value })} data-testid="job-issue">
          <option value="">None</option>
          {issues.map((i) => <option key={i.issue_id} value={i.issue_id}>{i.title}</option>)}
        </select>
      </div>
      <button className="btn-ink" type="submit" style={{ justifySelf: "start" }} data-testid="assign-job-btn">Assign job</button>
    </form>
  );
}

function JobsBoard({ jobs, properties, propFilter, setPropFilter, reload }) {
  const del = async (id) => { if (!window.confirm("Delete this job?")) return; try { await api.delete(`/jobs/${id}`); toast.success("Job deleted"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const reschedule = async (id, date) => { try { await api.patch(`/jobs/${id}`, { scheduled_date: date }); toast.success("Rescheduled · contractor and team notified"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const rate = async (id, rating, note) => { try { await api.post(`/jobs/${id}/rate`, { rating, note }); toast.success("Quality rating saved"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const groups = {};
  jobs.forEach((j) => { const k = j.scheduled_date || "No date set"; (groups[k] = groups[k] || []).push(j); });
  const keys = Object.keys(groups).sort((a, b) => (a === "No date set" ? 1 : b === "No date set" ? -1 : a.localeCompare(b)));
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div className="lbl">Contractor calendar</div>
        <select className="inp" style={{ maxWidth: 280 }} value={propFilter} onChange={(e) => setPropFilter(e.target.value)} data-testid="jobs-property-filter">
          <option value="">All properties</option>
          {properties.map((p) => <option key={p.property_id} value={p.property_id}>{p.ref ? `${p.ref} · ` : ""}{p.name}</option>)}
        </select>
      </div>
      {jobs.length === 0 && <Card><p style={{ color: "var(--muted)" }}>No jobs scheduled. Assign one above.</p></Card>}
      {keys.map((k) => (
        <div key={k}>
          <div className="lbl-m" style={{ color: "var(--honey)", marginBottom: 8 }}>{fmtDate(k === "No date set" ? null : k)}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {groups[k].map((j) => (
              <Card key={j.job_id} data-testid="staff-job-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 10 }}>
                  <div>
                    <b>{j.title}</b>{j.scheduled_time && <span className="pill-tag" style={{ marginLeft: 8, background: "var(--sand)", color: "var(--umber)" }}>{j.scheduled_time}</span>}
                    <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 3 }}>{j.property_name} · {j.contractor_name} ({j.contractor_trade})</div>
                    {j.description && <p style={{ fontSize: 13, marginTop: 6 }}>{j.description}</p>}
                    {j.status === "completed" && j.completion_note && <p style={{ fontSize: 13, marginTop: 6, color: "#5B6440" }}>Signed off: {j.completion_note}</p>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {j.status !== "completed" && <input type="date" value={j.scheduled_date || ""} onChange={(e) => reschedule(j.job_id, e.target.value)} data-testid="reschedule-date" title="Reschedule" style={{ padding: "4px 8px", fontSize: 12, border: "1px solid var(--line)", borderRadius: 6, background: "var(--paper)" }} />}
                    {j.scheduled_date && <a className="link-u" style={{ fontSize: 12 }} href={jobGcal(j)} target="_blank" rel="noreferrer" data-testid="job-cal-google">Google</a>}
                    {j.scheduled_date && <button className="link-u" style={{ fontSize: 12, background: "none", border: "none", cursor: "pointer", padding: 0 }} onClick={() => jobIcs(j)} data-testid="job-cal-apple">iCal</button>}
                    <StatusPill status={j.status === "completed" ? "completed" : "scheduled"} />
                    <button onClick={() => del(j.job_id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)" }} aria-label="Delete job"><X size={16} /></button>
                  </div>
                </div>
                {j.status === "completed" && (
                  <div style={{ marginTop: 10, borderTop: "1px solid var(--line)", paddingTop: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <span className="lbl-m">Quality</span>
                    <Stars value={j.rating || 0} onRate={(n) => rate(j.job_id, n, j.rating_note || null)} />
                    <input className="inp" style={{ maxWidth: 280, padding: "4px 8px", fontSize: 12 }} placeholder={j.rating ? "Add a note" : "Rate first, then add a note"} defaultValue={j.rating_note || ""} disabled={!j.rating} onBlur={(e) => { if (j.rating && e.target.value !== (j.rating_note || "")) rate(j.job_id, j.rating, e.target.value); }} data-testid="rating-note" />
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
